import type { BrokerAnswerRow, BrokerRequestDetailDto, BrokerUploadResultDto } from '@crm/shared'
import { createBrokerRequest, uploadBrokerAnswer } from '../../../shared/api/customs.api'
import {
  brokerAnswersFileName,
  brokerFileName,
  brokerRemainderFileName,
  buildBrokerWorkbook,
  saveWorkbookAsFile,
} from './brokerExcel'
import { parseBrokerResponse } from './parseBrokerResponse'

/**
 * Обмен с брокером по нетарифке (Л2): файл брокеру и загрузка его ответа.
 * Кнопки — в тулбаре очереди «Нетарифка» и на странице запроса брокеру.
 */

/** Больше строк сервер не примет за один раз (UploadBrokerAnswerSchema). */
export const BROKER_UPLOAD_MAX_ROWS = 2000
/** Сколько партномеров «не найдено» перечислять в тосте. */
const UNMATCHED_PREVIEW = 5

/** Скачать Excel запроса брокеру (сразу после создания или повторно из журнала). */
export async function downloadBrokerFile(detail: BrokerRequestDetailDto): Promise<void> {
  const wb = buildBrokerWorkbook(detail)
  await saveWorkbookAsFile(wb, brokerFileName(detail.fileCode))
}

/** «Скачать с ответами»: тот же файл, колонки брокера заполнены записанными ответами. */
export async function downloadBrokerAnswers(detail: BrokerRequestDetailDto): Promise<number> {
  const answered = detail.items.filter((i) => i.answer != null).length
  if (answered === 0) return 0
  const wb = buildBrokerWorkbook(detail, { withAnswers: true })
  await saveWorkbookAsFile(wb, brokerAnswersFileName(detail.fileCode))
  return answered
}

/** Партномера запроса, по которым брокер ещё не ответил. */
export function pendingBrokerItems(
  detail: BrokerRequestDetailDto,
): BrokerRequestDetailDto['items'] {
  return detail.items.filter((i) => i.status === 'PENDING')
}

/**
 * «Скачать остатки»: файл только с партномерами без ответа — брокер ответил
 * частично. Код запроса в файле тот же, поэтому ответ по остаткам загружается
 * в этот же запрос (как у закупщика — остатки запроса поставщику).
 */
export async function downloadBrokerRemainder(detail: BrokerRequestDetailDto): Promise<number> {
  const items = pendingBrokerItems(detail)
  if (items.length === 0) return 0
  const wb = buildBrokerWorkbook({ fileCode: detail.fileCode, items })
  await saveWorkbookAsFile(wb, brokerRemainderFileName(detail.fileCode))
  return items.length
}

/**
 * «Отправить брокеру»: сервер создаёт запрос (позиции → «Отправлено брокеру»,
 * уникальные партномера), сайт собирает и скачивает файл. Если запрос создан,
 * а файл не скачался — ошибка с кодом запроса: файл можно скачать из журнала.
 */
export async function exportToBroker(params: {
  itemIds: string[]
  note?: string
  brokerId?: string | null
}): Promise<BrokerRequestDetailDto> {
  const note = params.note?.trim()
  const detail = await createBrokerRequest({
    itemIds: params.itemIds,
    ...(note ? { note } : {}),
    ...(params.brokerId ? { brokerId: params.brokerId } : {}),
  })
  try {
    await downloadBrokerFile(detail)
  } catch {
    throw new Error(
      `Запрос ${detail.fileCode} создан, но файл не скачался — скачайте его в журнале «Запросы брокеру»`,
    )
  }
  return detail
}

export interface BrokerUploadOutcome {
  result: BrokerUploadResultDto
  /** Непустые строки файла без артикула. */
  skippedRows: number
  /** Строки с артикулом без ответа брокера — отсеяны на сайте, на сервер не ушли. */
  emptyRows: number
  /** Строки файла, ушедшие на сервер, — для ручной привязки ненайденных артикулов. */
  rows: BrokerAnswerRow[]
}

/**
 * «Загрузить ответ брокера»: разобрать Excel и отправить строки на сервер.
 * Если в файле есть код запроса (брокер ответил в нашем файле) — доверяем ему:
 * так ответ ляжет в свой запрос, даже если файл загрузили со страницы другого.
 * Нет кода — берём запрос страницы, а без него сервер ищет по всем открытым.
 */
export async function uploadBrokerFile(
  file: File,
  brokerRequestId?: string,
): Promise<BrokerUploadOutcome> {
  const parsed = await parseBrokerResponse(file)
  if (parsed.rows.length === 0) {
    throw new Error('В файле нет строк с артикулом — проверьте, что это ответ брокера')
  }
  if (parsed.rows.length > BROKER_UPLOAD_MAX_ROWS) {
    throw new Error(`В файле больше ${BROKER_UPLOAD_MAX_ROWS} строк — разделите его на части`)
  }
  const target = parsed.fileCode
    ? { fileCode: parsed.fileCode }
    : brokerRequestId
      ? { brokerRequestId }
      : {}
  const result = await uploadBrokerAnswer({ ...target, rows: parsed.rows })
  return { result, skippedRows: parsed.skipped, emptyRows: parsed.emptyRows, rows: parsed.rows }
}

/** «позиция / позиции / позиций» по числу. */
export function positionsWord(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'позиция'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'позиции'
  return 'позиций'
}

/** «1 строка», «3 строки», «12 строк». */
export function rowsText(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} строка`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} строки`
  return `${n} строк`
}

/** Итог загрузки для тоста: тон и текст. */
export function brokerUploadSummary(
  outcome: Pick<BrokerUploadOutcome, 'result' | 'skippedRows' | 'emptyRows'>,
): {
  tone: 'success' | 'info' | 'error'
  text: string
} {
  const { result, skippedRows } = outcome
  const { unmatched } = result
  // Без ответа: часть отсеял сайт, часть — сервер (ТН ВЭД совпал с отправленным).
  const emptyTotal = outcome.emptyRows + result.emptyRows
  const emptyText = emptyTotal > 0 ? `без ответа брокера: ${rowsText(emptyTotal)}` : ''
  const unmatchedText =
    unmatched.length > 0
      ? `не найдено в запросах: ${unmatched.length} (${unmatched.slice(0, UNMATCHED_PREVIEW).join(', ')}${
          unmatched.length > UNMATCHED_PREVIEW ? '…' : ''
        })`
      : ''
  const skippedText = skippedRows > 0 ? `строк без артикула пропущено: ${skippedRows}` : ''
  const aliasedCount = result.aliased?.length ?? 0
  const aliasedText =
    aliasedCount > 0 ? `брокер записал артикул иначе, узнали: ${aliasedCount}` : ''
  const completeText =
    (result.alreadyComplete ?? 0) > 0
      ? `уже было записано полностью: ${rowsText(result.alreadyComplete ?? 0)}`
      : ''
  const notAppliedCount = result.notApplied?.length ?? 0
  const notAppliedText =
    notAppliedCount > 0
      ? `не легло на позиции (партномер поправили после отправки): ${notAppliedCount}`
      : ''

  if (result.matchedRows === 0) {
    const head =
      unmatched.length === 0 && emptyTotal > 0
        ? 'В файле нет ответов брокера'
        : 'Ни одна строка файла не совпала с запросами брокеру'
    const parts = [head, unmatchedText, emptyText]
    return { tone: 'error', text: parts.filter(Boolean).join('; ') }
  }

  const filled = result.filledPositions ?? 0
  const head =
    result.positionsUpdated === 0 && filled > 0
      ? `Дозаполнены прошлые ответы: ${filled} ${positionsWord(filled)}`
      : result.positionsUpdated === 0
        ? 'Новых данных нет: всё из файла уже записано'
        : `Ответ разложен: ${result.positionsUpdated} ${positionsWord(result.positionsUpdated)}`
  const parts = [
    head,
    result.positionsUpdated > 0 ? `с флагом «внимание»: ${result.attentionPositions}` : '',
    result.positionsUpdated > 0 && filled > 0
      ? `дозаполнены прошлые ответы: ${filled} ${positionsWord(filled)}`
      : '',
    aliasedText,
    completeText,
    notAppliedText,
    unmatchedText,
    emptyText,
    skippedText,
  ]
  const codes = result.fileCodes
  const requests =
    codes.length === 0 ? '' : `. ${codes.length === 1 ? 'Запрос' : 'Запросы'}: ${codes.join(', ')}`
  return {
    tone:
      unmatched.length > 0 || skippedRows > 0 || emptyTotal > 0 || notAppliedCount > 0
        ? 'info'
        : 'success',
    text: parts.filter(Boolean).join('; ') + requests,
  }
}
